// ─── Primitive Union Types ─────────────────────────────────────────────────────

export type TaskStatus = 'pending' | 'in_progress' | 'submitted' | 'revision_needed' | 'approved' | 'rejected' | 'final_accepted' | 'final_rejected';
export type SpeakerType = 'enumerator' | 'respondent' | 'unknown' | 'participant_1' | 'participant_2' | 'participant_3';
export type TranscriptionStatus = 'none' | 'queued' | 'running' | 'completed' | 'failed';
export type ConfidenceLevel = 'high' | 'medium' | 'low';
export type UtteranceSource = 'manual' | 'auto_accepted' | 'auto_corrected';
export type EmotionType = 'neutral' | 'angry' | 'sad' | 'happy' | 'fearful' | 'sarcastic' | 'frustrated' | 'hesitant' | 'confident' | 'excited' | 'disappointed';
export type SentimentType = 'positive' | 'negative' | 'neutral' | 'mixed';
export type IntentType = 'complaint' | 'praise' | 'opinion' | 'demand' | 'narrative' | 'comparison' | 'explanation' | 'request' | 'question' | 'agreement' | 'disagreement';
export type SpeechSpeed = 'slow' | 'normal' | 'fast' | 'very_fast';
export type AudioQuality = 'clear' | 'low_background_noise' | 'moderate_noise' | 'heavy_noise' | 'wind_noise' | 'microphone_distortion';
export type SpeechMarker = 'pause' | 'long_pause' | 'hesitation' | 'laughter' | 'sigh' | 'interruption' | 'overlap' | 'emphasis';
export type EntityType = 'person' | 'organization' | 'post_designation' | 'government_institution' | 'policy' | 'issue' | 'location' | 'programme_scheme' | 'thing_object';
export type EntitySentiment = 'positive' | 'negative' | 'neutral' | 'mixed';
export type WordLabel = 'emphasis' | 'entity_mention' | 'mispronunciation';
export type PhotoType = 'house_exterior' | 'house_interior' | 'street_view' | 'community_area' | 'open_area' | 'landmark';
export type SettlementType = 'dense_urban' | 'urban' | 'small_town' | 'semi_urban' | 'village' | 'remote_rural';
export type HousingType = 'apartment_flat' | 'plastered_house' | 'brick_house' | 'mixed_construction' | 'mud_house' | 'temporary_structure' | 'slum_dwelling';
export type HouseCondition = 'excellent' | 'good' | 'average' | 'poor' | 'dilapidated';
export type RoofType = 'concrete_slab' | 'tin_metal_sheet' | 'tile_roof' | 'thatch' | 'mixed_materials';
export type StreetType = 'rcc_road' | 'paved_road' | 'dirt_road' | 'narrow_lane' | 'highway';
export type VehiclePresence = 'none' | 'bicycle' | 'motorcycle' | 'auto_rickshaw' | 'car' | 'multiple_vehicles';
export type CleanlinessLevel = 'very_clean' | 'moderately_clean' | 'some_litter' | 'dirty' | 'heavily_polluted';
export type PopulationDensity = 'sparse' | 'moderate' | 'dense' | 'very_dense';
export type EconomicActivity = 'none' | 'agriculture' | 'small_shops' | 'street_vendors' | 'market_area' | 'industrial_activity';
export type ImageQuality = 'clear' | 'blurred' | 'low_light' | 'overexposed' | 'partially_obstructed';
export type IncomeProxy = 'very_low' | 'low' | 'lower_middle' | 'middle' | 'upper_middle';

// ─── Transcription Types ───────────────────────────────────────────────────────

export interface WordTimestamp {
  word: string;
  start: number;
  end: number;
  probability: number;
  speaker: string;
}

export interface TranscriptionJob {
  jobId: string;
  taskId: string;
  status: TranscriptionStatus;
  modelName: string;
  language: string;
  maxSpeakers: number;
  numSpeakersDetected: number | null;
  s3OutputKey: string | null;
  triggeredBy: string;
  triggeredAt: string;
  startedAt: string | null;
  completedAt: string | null;
  audioDurationS: number | null;
  processingTimeS: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface AutoUtterance {
  autoUtteranceId: string;
  jobId: string;
  taskId: string;
  sessionId: string;
  speakerLabel: string;
  startTime: number;
  endTime: number;
  transcript: string;
  confidenceScore: number;
  confidenceLevel: ConfidenceLevel;
  isLongSegment: boolean;
  isCodeMixed: boolean;
  sequenceIndex: number;
  wordTimestamps: WordTimestamp[] | null;
  createdAt: string;
}

// ─── Entity ────────────────────────────────────────────────────────────────────

export interface Entity {
  entityId: string;
  utteranceId: string;
  entityType: EntityType;
  entityName: string;
  sentiment?: EntitySentiment;
  createdAt: string;
}

// ─── Utterance ─────────────────────────────────────────────────────────────────

export interface Utterance {
  utteranceId: string;
  taskId: string;
  sessionId: string;
  speaker: SpeakerType;
  startTime: number;
  endTime: number;
  sequenceIndex: number;
  transcript: string | null;
  normalizedTranscript: string | null;
  emotion: EmotionType | null;
  sentiment: SentimentType | null;
  intent: IntentType | null;
  speechSpeed: SpeechSpeed | null;
  audioQuality: AudioQuality | null;
  speechMarkers: SpeechMarker[];
  annotatorId: string;
  annotatorConfidence: number | null;
  source: UtteranceSource;
  autoUtteranceId: string | null;
  lastEditedBy: string | null;
  lastEditedAt: string | null;
  entities: Entity[];
  createdAt: string;
  updatedAt: string;
}

// ─── Photo Labels ──────────────────────────────────────────────────────────────

export interface PhotoLabels {
  labelId: string;
  photoId: string;
  annotatorId: string;
  description: string | null;
  settlementType: SettlementType | null;
  housingType: HousingType | null;
  houseCondition: HouseCondition | null;
  roofType: RoofType | null;
  streetType: StreetType | null;
  infrastructure: string[];
  objectPresence: string[];
  vehicles: VehiclePresence | null;
  cleanliness: CleanlinessLevel | null;
  populationDensity: PopulationDensity | null;
  economicActivity: EconomicActivity | null;
  imageQuality: ImageQuality | null;
  incomeProxy: IncomeProxy | null;
  privacyFlag: boolean;
  supervisorVerified: boolean;
  supervisorId?: string;
  supervisorNotes?: string;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Photo ─────────────────────────────────────────────────────────────────────

export interface Photo {
  photoId: string;
  sessionId: string;
  taskId: string;
  s3Key: string;
  photoType: PhotoType | null;
  sequenceIndex: number;
  url?: string;
  labels?: PhotoLabels;
  createdAt: string;
}

// ─── Transcript Image ──────────────────────────────────────────────────────────

export interface TranscriptImage {
  transcriptId: string;
  taskId: string;
  sessionId: string;
  s3Key: string;
  pageNumber: number;
  url?: string;
  createdAt: string;
}

// ─── Task Review ───────────────────────────────────────────────────────────────

export interface TaskReview {
  reviewId: string;
  taskId: string;
  supervisorId: string;
  status: 'approved' | 'rejected' | 'revision_needed';
  comments: string | null;
  createdAt: string;
}

// ─── Annotation Task ───────────────────────────────────────────────────────────

export interface AnnotationTask {
  taskId: string;
  sessionId: string;
  projectId: string;
  enumeratorId: string;
  supervisorId: string | null;
  status: TaskStatus;
  sessionLocation?: { state: string; district: string; village: string };
  enumeratorName?: string;
  // The server-attached enumerator identity fields (list-tasks fetches
  // these from the Users table per task) — present even for indirectly-
  // visible users that wouldn't appear in a supervisor's scoped /users
  // call, so they're the reliable source for the enumerator column.
  enumeratorEmail?: string | null;
  enumeratorRole?: string | null;
  enumeratorPhone?: string | null;
  duration?: number;
  respondentName?: string;
  language?: string;
  videoUrl?: string;
  audioUrl?: string;
  peaksUrl?: string;
  transcriptionStatus: TranscriptionStatus;
  latestJobId: string | null;
  utterances: Utterance[];
  photos: Photo[];
  transcripts: TranscriptImage[];
  latestReview?: TaskReview;
  createdAt: string;
  startedAt?: string;
  submittedAt?: string;
  submittedBy?: string | null;
  submittedByName?: string | null;
  submittedByEmail?: string | null;
  submittedByRole?: string | null;
  submittedByPhone?: string | null;
  claimedBy?: string | null;
  claimedByName?: string | null;
  claimedByEmail?: string | null;
  claimedByRole?: string | null;
  claimedByPhone?: string | null;
  reviewedAt?: string;
  updatedAt: string;
}

// ─── Supervisor Assignment ─────────────────────────────────────────────────────

export interface SupervisorAssignment {
  assignmentId: string;
  enumeratorId: string;
  supervisorId: string;
  assignedBy: string;
  createdAt: string;
  enumeratorName?: string;
  supervisorName?: string;
}

// ─── Display Helper Types ──────────────────────────────────────────────────────

export interface TaskListItem extends Omit<AnnotationTask, 'utterances' | 'photos' | 'transcripts'> {
  utteranceCount?: number;
}

// ─── Workspace State ───────────────────────────────────────────────────────────

export interface AnnotationWorkspaceState {
  task: AnnotationTask | null;
  selectedUtteranceId: string | null;
  activePanel: 'transcript' | 'annotation' | 'photo';
  isDirty: boolean;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
  autoSavedAt: Date | null;
}

// ─── Display Label Maps ────────────────────────────────────────────────────────

export const EMOTION_LABELS: Record<EmotionType, string> = {
  neutral: 'Neutral',
  angry: 'Angry',
  sad: 'Sad',
  happy: 'Happy',
  fearful: 'Fearful',
  sarcastic: 'Sarcastic',
  frustrated: 'Frustrated',
  hesitant: 'Hesitant',
  confident: 'Confident',
  excited: 'Excited',
  disappointed: 'Disappointed',
};

export const SENTIMENT_LABELS: Record<SentimentType, string> = {
  positive: 'Positive',
  negative: 'Negative',
  neutral: 'Neutral',
  mixed: 'Mixed',
};

export const INTENT_LABELS: Record<IntentType, string> = {
  complaint: 'Complaint',
  praise: 'Praise',
  opinion: 'Opinion',
  demand: 'Demand',
  narrative: 'Narrative',
  comparison: 'Comparison',
  explanation: 'Explanation',
  request: 'Request',
  question: 'Question',
  agreement: 'Agreement',
  disagreement: 'Disagreement',
};

export const SPEECH_SPEED_LABELS: Record<SpeechSpeed, string> = {
  slow: 'Slow',
  normal: 'Normal',
  fast: 'Fast',
  very_fast: 'Very Fast',
};

export const AUDIO_QUALITY_LABELS: Record<AudioQuality, string> = {
  clear: 'Clear',
  low_background_noise: 'Low Background Noise',
  moderate_noise: 'Moderate Noise',
  heavy_noise: 'Heavy Noise',
  wind_noise: 'Wind Noise',
  microphone_distortion: 'Microphone Distortion',
};

export const SPEECH_MARKER_LABELS: Record<SpeechMarker, string> = {
  pause: 'Pause',
  long_pause: 'Long Pause',
  hesitation: 'Hesitation',
  laughter: 'Laughter',
  sigh: 'Sigh',
  interruption: 'Interruption',
  overlap: 'Overlap',
  emphasis: 'Emphasis',
};

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  person: 'Person',
  organization: 'Organization',
  post_designation: 'Post / Designation',
  government_institution: 'Government Institution',
  policy: 'Policy',
  issue: 'Issue',
  location: 'Location',
  programme_scheme: 'Programme / Scheme',
  thing_object: 'Thing / Object',
};

export const PHOTO_TYPE_LABELS: Record<PhotoType, string> = {
  house_exterior: 'House Exterior',
  house_interior: 'House Interior',
  street_view: 'Street View',
  community_area: 'Community Area',
  open_area: 'Open Area',
  landmark: 'Landmark',
};

export const SPEAKER_TYPE_LABELS: Record<SpeakerType, string> = {
  enumerator: 'Enumerator',
  respondent: 'Respondent',
  unknown: 'Unknown',
  participant_1: 'Participant 1',
  participant_2: 'Participant 2',
  participant_3: 'Participant 3',
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  revision_needed: 'Revision Needed',
  approved: 'Supervisor Approved',
  rejected: 'Rejected',
  final_accepted: 'Final Accepted',
  final_rejected: 'Final Rejected',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  revision_needed: 'bg-orange-100 text-orange-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  final_accepted: 'bg-emerald-600 text-white',
  final_rejected: 'bg-rose-700 text-white',
};

// ─── Predefined Issue Taxonomy ─────────────────────────────────────────────────

export const PREDEFINED_ISSUES = [
  'Inflation',
  'Jobs',
  'Agriculture',
  'Healthcare',
  'Education',
  'Corruption',
  'Law and order',
  'Infrastructure',
  'Electricity',
  'Water supply',
  'Housing',
  'Transport',
  'Local governance',
  'Welfare schemes',
  'Taxes',
];
